import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './SubmitYourMusicPage.css';
import './SubmitYourMusicGuidedPreviewPage.css';

const SUBMIT_BACKGROUND_GIF = `${process.env.PUBLIC_URL}/static/img/playlist-anim.gif`;

const FEEDBACK_PREVIEW_MEDIA = [
  { type: 'video', src: require('./feedback/00_feedback.mp4') },
  { type: 'image', src: require('./feedback/01_feedback.jpg') },
  { type: 'video', src: require('./feedback/02_feedback.mp4') },
];

const PLAN_SUMMARIES = {
  quick_check: {
    title: 'Quick Check',
    body: 'Brief written review and independent curator decision. Up to 5 days.',
  },
  standard: {
    title: 'Standard',
    body: 'Written feedback included. Up to 5 days.',
  },
  premium: {
    title: 'Premium',
    body: 'Priority review. Up to 48 hours. More detailed feedback.',
  },
};

function getPriceDetails(selectedPlan) {
  switch (selectedPlan) {
    case 'standard_1':
      return { price: '$13', discount: '' };
    case 'standard_5':
      return { price: '$60', discount: 'Save $5' };
    case 'standard_10':
      return { price: '$110', discount: 'Save $20' };
    case 'premium_1':
      return { price: '$27', discount: '' };
    case 'premium_5':
      return { price: '$125', discount: 'Save $10' };
    case 'premium_10':
      return { price: '$230', discount: 'Save $40' };
    case 'quick_check':
      return { price: '$5', discount: '' };
    default:
      return { price: '', discount: '' };
  }
}

function getPlanGroup(plan) {
  if (!plan) return '';
  if (plan === 'quick_check') return 'quick_check';
  if (plan.startsWith('standard_')) return 'standard';
  if (plan.startsWith('premium_')) return 'premium';
  return '';
}

const GuidedSubmissionButtons = ({ selectedPlan, onClick }) => {
  const renderTier = (tier, label, planPrefix, buttonExtraClass = '', titleClass = '') => (
    <div className={`submission-option ${tier}-column`}>
      <div className={`option-title ${titleClass}`.trim()}>{label}</div>
      <div className="option-buttons">
        {['1', '5', '10'].map((n) => {
          const plan = `${planPrefix}_${n}`;
          const isSelected = selectedPlan === plan;
          const classes = [
            'submission-button',
            buttonExtraClass,
            isSelected ? 'selected' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={`${n}-${tier}`}
              type="button"
              data-plan={plan}
              className={classes}
              onClick={() => onClick(plan)}
            >
              <span className="btn-glass" aria-hidden="true"></span>
              <span className="btn-label">
                <span className="big">{n} {tier === 'premium' ? 'Premium' : 'Standard'}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="submission-buttons">
      <div className="submission-option quick-check-wrap">
        <button
          type="button"
          data-plan="quick_check"
          className={`submission-button quick ${selectedPlan === 'quick_check' ? 'selected' : ''}`}
          onClick={() => onClick('quick_check')}
        >
          <span className="btn-glass" aria-hidden="true"></span>
          <span className="btn-label">
            <span className="big">Quick Check</span>
          </span>
        </button>
      </div>

      <div className="submission-options-duo">
        {renderTier('standard', 'Standard submission', 'standard')}
        {renderTier('premium', 'Premium submission', 'premium', 'premium', 'premium-title')}
      </div>
    </div>
  );
};

const FeedbackPreviewModal = ({ onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal guided-preview-modal" onClick={(event) => event.stopPropagation()}>
      <button className="close-button" onClick={onClose}>
        ×
      </button>
      <h2>Artist feedback preview</h2>
      <p className="guided-preview-modal__copy">
        This is a lightweight preview inside the prototype. The real version could open the full feedback gallery without leaving the flow.
      </p>
      <div className="guided-feedback-grid">
        {FEEDBACK_PREVIEW_MEDIA.map((item, index) => (
          <div key={`${item.type}-${index}`} className="guided-feedback-card">
            {item.type === 'image' ? (
              <img src={item.src} alt={`Artist feedback preview ${index + 1}`} className="guided-feedback-media" />
            ) : (
              <video
                src={item.src}
                className="guided-feedback-media"
                autoPlay
                muted
                loop
                playsInline
              />
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
);

function SubmitYourMusicGuidedPreviewPage() {
  const navigate = useNavigate();
  const [{ step }, setNav] = useState({ step: 'intro', history: [] });
  const [selectedPlan, setSelectedPlan] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [showFeedbackPreview, setShowFeedbackPreview] = useState(false);

  const goTo = (nextStep) => {
    setNav((current) => ({
      step: nextStep,
      history: [...current.history, current.step],
    }));
  };

  const goBack = () => {
    setNav((current) => {
      const nextHistory = [...current.history];
      const previousStep = nextHistory.pop() || 'intro';
      return { step: previousStep, history: nextHistory };
    });
  };

  const currentStepNumber = step === 'plans' ? 3 : step === 'fit' || step === 'mismatch' ? 2 : 1;
  const { price, discount } = useMemo(() => getPriceDetails(selectedPlan), [selectedPlan]);
  const selectedPlanGroup = getPlanGroup(selectedPlan);
  const canContinueToSubmit = Boolean(selectedPlan && agreed);

  const openPlaylists = () => {
    window.open('/playlists', '_blank', 'noopener,noreferrer');
  };

  const handlePreviewSubmit = (event) => {
    event.preventDefault();
    if (!canContinueToSubmit) {
      return;
    }
    navigate('/submit-your-music');
  };

  return (
    <div className="App submit-page guided-submit-preview">
      <div id="background">
        <img src={require('./bg_noise/mobile_noise.gif')} alt="" aria-hidden="true" className="bg-gif mobile" />
        <img src={require('./bg_noise/tablet_noise.gif')} alt="" aria-hidden="true" className="bg-gif tablet" />
        <img src={require('./bg_noise/desktop_noise.gif')} alt="" aria-hidden="true" className="bg-gif desktop" />
        <img src={SUBMIT_BACKGROUND_GIF} alt="" aria-hidden="true" id="animated-gif" />
      </div>

      <div id="content-wrapper">
        <div id="links" className="submit-links guided-preview-shell">
          <div className="guided-preview-badge">Private preview</div>
          <div className="section-title hero-title" data-text="GUIDED SUBMISSION PREVIEW">
            GUIDED SUBMISSION PREVIEW
          </div>

          <div className="guided-preview-progress">
            <span>Step {currentStepNumber} / 3</span>
            <button
              type="button"
              className="guided-preview-progress__exit"
              onClick={() => navigate('/submit-your-music')}
            >
              Open current page
            </button>
          </div>

          {step === 'intro' && (
            <section className="guided-stage">
              <div className="guided-stage__eyebrow">First-time qualification</div>
              <h1 className="guided-stage__title">Hi, it&apos;s Iva.</h1>
              <p className="guided-stage__copy">
                This service is for track review and possible playlist consideration.
              </p>
              <p className="guided-stage__copy">
                Before anyone buys, I want the expectations to be clear.
              </p>
              <div className="guided-stage__question">Have you submitted music to me before?</div>
              <div className="guided-choice-grid">
                <button type="button" className="guided-choice guided-choice--primary" onClick={() => goTo('plans')}>
                  Yes, I have
                </button>
                <button type="button" className="guided-choice" onClick={() => goTo('fit')}>
                  No, first time here
                </button>
              </div>
              <div className="guided-stage__footnote">Returning artists can skip straight to plan selection.</div>
            </section>
          )}

          {step === 'fit' && (
            <section className="guided-stage">
              <div className="guided-stage__eyebrow">Expectation check</div>
              <h1 className="guided-stage__title">One important thing first.</h1>
              <p className="guided-stage__copy">
                If you want guaranteed placement or guaranteed streams, this is not the right service.
              </p>
              <p className="guided-stage__copy">
                I only offer real curation, real feedback, and possible playlist consideration when a track fits.
              </p>
              <div className="guided-stage__question">Are you okay with that?</div>
              <div className="guided-choice-grid">
                <button type="button" className="guided-choice guided-choice--primary" onClick={() => goTo('plans')}>
                  Yes, I understand
                </button>
                <button type="button" className="guided-choice guided-choice--danger" onClick={() => goTo('mismatch')}>
                  No, I want guaranteed placement / streams
                </button>
              </div>
              <div className="guided-stage__nav">
                <button type="button" className="guided-stage__back" onClick={goBack}>
                  Back
                </button>
              </div>
            </section>
          )}

          {step === 'mismatch' && (
            <section className="guided-stage">
              <div className="guided-stage__eyebrow">Expectation mismatch</div>
              <h1 className="guided-stage__title">Then this is probably not the right fit.</h1>
              <p className="guided-stage__copy">
                I understand why that sounds appealing. But guaranteed placement weakens playlists, and guaranteed streams are artificial boosting.
              </p>
              <p className="guided-stage__copy">
                Paid guaranteed placement and fake streaming also go against platform rules.
              </p>
              <div className="guided-choice-grid guided-choice-grid--stack">
                <button type="button" className="guided-choice guided-choice--primary" onClick={() => goTo('plans')}>
                  I still want to continue
                </button>
                <button type="button" className="guided-choice" onClick={openPlaylists}>
                  See playlists first
                </button>
              </div>
              <div className="guided-inline-links">
                <button type="button" className="guided-inline-links__item" onClick={() => setShowFeedbackPreview(true)}>
                  Read artist feedback
                </button>
                <button type="button" className="guided-inline-links__item" onClick={goBack}>
                  Go back
                </button>
              </div>
            </section>
          )}

          {step === 'plans' && (
            <section className="guided-stage guided-stage--plans">
              <div className="guided-stage__eyebrow">Plan selection</div>
              <h1 className="guided-stage__title">Good. Here&apos;s the simple version.</h1>
              <p className="guided-stage__copy">
                Choose the option that fits how fast you want a decision and how much feedback you want.
              </p>

              <div className="guided-plan-copy" aria-label="Plan summary">
                <div className={`guided-plan-copy__item ${selectedPlanGroup === 'quick_check' ? 'is-active' : ''}`}>
                  <div className="guided-plan-copy__title">{PLAN_SUMMARIES.quick_check.title}</div>
                  <div className="guided-plan-copy__text">{PLAN_SUMMARIES.quick_check.body}</div>
                </div>
                <div className={`guided-plan-copy__item ${selectedPlanGroup === 'standard' ? 'is-active' : ''}`}>
                  <div className="guided-plan-copy__title">{PLAN_SUMMARIES.standard.title}</div>
                  <div className="guided-plan-copy__text">{PLAN_SUMMARIES.standard.body}</div>
                </div>
                <div className={`guided-plan-copy__item ${selectedPlanGroup === 'premium' ? 'is-active' : ''}`}>
                  <div className="guided-plan-copy__title">{PLAN_SUMMARIES.premium.title}</div>
                  <div className="guided-plan-copy__text">{PLAN_SUMMARIES.premium.body}</div>
                </div>
              </div>

              <div className="guided-preview-trust">
                Real curation only — no bots, no fake streams, no guaranteed placement.
              </div>

              <div className="permanent-show-section guided-preview-plan-picker">
                <GuidedSubmissionButtons selectedPlan={selectedPlan} onClick={setSelectedPlan} />
                {selectedPlan ? (
                  <>
                    <div className="price-details">
                      <p>
                        Price: <span className="price-glow">{price}</span>{' '}
                        {discount && <span className="discount-glow">({discount})</span>}
                      </p>
                    </div>
                    <form className="submission-form guided-preview-form" onSubmit={handlePreviewSubmit}>
                      <label className="submission-terms-label guided-preview-terms">
                        <input
                          type="checkbox"
                          checked={agreed}
                          onChange={(event) => setAgreed(event.target.checked)}
                          required
                        />{' '}
                        I understand this is a paid review service with possible playlist consideration, not guaranteed placement or guaranteed streams.
                      </label>
                      <button type="submit" className={canContinueToSubmit ? 'enabled' : ''} disabled={!canContinueToSubmit}>
                        <span className="btn-glass" aria-hidden="true"></span>
                        <span className="btn-label">Open current submit flow</span>
                      </button>
                    </form>
                  </>
                ) : (
                  <div className="guided-preview-select-note">Select a plan to continue.</div>
                )}
              </div>

              <div className="guided-stage__nav guided-stage__nav--spread">
                <button type="button" className="guided-stage__back" onClick={goBack}>
                  Back
                </button>
                <div className="guided-stage__footnote">
                  Preview only. This route does not process payment yet.
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {showFeedbackPreview && <FeedbackPreviewModal onClose={() => setShowFeedbackPreview(false)} />}
    </div>
  );
}

export default SubmitYourMusicGuidedPreviewPage;
